// ═══ 거래처 스코프 검색 콤보 (리빌드 R3) — 그룹(브로커) + 검색=내비게이션 ═══
// 정본 UX: erp/app/docs/settlement-ledger/zero-base-design.md §6.1
//
// props:
//   value      '' | 'u:<unitId>' | 'a:<accountId>'   ('' = 전체)
//   data       { groups:[{account:{id,kind,name,balance,unitsCount}, units:[{id,name,balance}]}],
//                directs:[{accountId,unitId,name,balance}] }
//   onChange   (value) => void
//   placeholder (선택)
//
// 동작 계약:
//  - 검색어 입력 시 하위 유닛까지 평면 매칭, 소속 꼬리표 '이름 · 그룹명' 표시 (§6.1)
//  - 목록(검색어 없음): (전체 거래처) → 그룹 헤더 행 '온커머스 N곳 · 미수 X' (탭=a:스코프)
//    → 그룹 하위 들여쓰기(기본 접힘 — ▸ 토글로 펼침) → 직거래처 평면
//  - 키보드 (Jin 9/2): 검색 결과에서 ↑/↓ 로 하이라이트 이동(비순환, keydown + preventDefault 로
//    커서 점프 차단), Enter = 하이라이트 항목 pick, **이동 전 Enter 는 최상단(filtered[0]) pick**,
//    Esc = 닫기. 검색어가 바뀌면 하이라이트 리셋.
//  - ⚠ Enter 는 계속 **keyup** 이다. 한글 IME 조합 확정 엔터는 keydown 이 isComposing 으로
//    소비돼 안 잡힌다 (CancelAgencySettlement.js 의 기확립 패턴). 화살표/Esc 만 keydown 으로
//    올린다 — 조합 중에도 커서 이동을 막아야 하고 IME 와 무관하기 때문.
//    엔터는 결과 수와 무관하게 무조건 blur (조건부 렌더 && 숫자 0 누출 금지).
import React, { useState, useRef, useEffect } from 'react';
import { useIsMobile } from '../utils/useMobile';

// 금액은 만원 축약 없이 원 단위 그대로 (§6.1 — 잔액 0 도 표기).
// 값은 호출자(CancelAgencySettlement scopeData)가 넘긴 것을 그대로 표시 —
// 2026-08-11 부터 그 값은 '확정 미수'(잔액 − 이번 달 청구) 다. 이 컴포넌트는 계산하지 않는다.
// won/balColor/flattenScopeOptions 는 상주 패널(ClientScopePanel)과 공유 — 표기 규약 단일 원천.
// 미수/잔액은 절대값 표기 — 부호는 색이 대신한다 (빨강=미수, 초록=선수금·완납. Jin 8/12)
export var won = function (n) { return Math.abs(parseInt(n, 10) || 0).toLocaleString() + '원'; };
// 미수 색 — 양수 red / 음수(선입금) green / 0 무채
export var balColor = function (n) {
  var v = parseInt(n, 10) || 0;
  return v > 0 ? '#dc2626' : (v < 0 ? '#059669' : '#94a3b8');
};

var wrap = { position: 'relative', minWidth: 220 };
var inputSt = { padding: '8px 12px', border: '1px solid #d0d5dd', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' };
// 드롭다운은 입력창보다 넓게(최소 360px) — 좁은 폼 칸에서 '263곳' 등이 줄바꿈되는 것 방지 (Jin 8/12)
var listSt = { position: 'absolute', top: '100%', left: 0, minWidth: '100%', width: 380, maxWidth: '80vw', zIndex: 100, background: '#fff', border: '1px solid #d0d5dd', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 320, overflowY: 'auto', marginTop: 2 };
var rowSt = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };
var groupRowSt = Object.assign({}, rowSt, { background: '#eef2f7', fontWeight: 700, color: '#1e3a5f' });
// 하위 업체 — 그룹 레일(좌측 3px 연결선) + 옅은 배경으로 직거래처와 구분
var unitRowSt = Object.assign({}, rowSt, { paddingLeft: 33, borderLeft: '3px solid #60a5fa', background: '#f5f9ff', fontSize: 12.5 });
var toggleSt = { width: 16, flexShrink: 0, background: 'none', border: 'none', padding: 0, fontSize: 12, color: '#475569', cursor: 'pointer', lineHeight: 1 };
var tailSt = { fontSize: 11, color: '#94a3b8' };
var amtSt = { marginLeft: 'auto', fontSize: 12, whiteSpace: 'nowrap' };

// 계정/유닛을 검색용 평면 옵션으로 — v(스코프 값) · name · group(소속 꼬리표) · balance · kind
export var flattenScopeOptions = function (groups, directs) {
  var out = [];
  groups.forEach(function (g) {
    var acc = (g && g.account) || {};
    out.push({ v: 'a:' + acc.id, name: acc.name || '', group: '', balance: acc.balance, kind: 'group', unitsCount: acc.unitsCount || (g.units || []).length });
    (g.units || []).forEach(function (u) {
      out.push({ v: 'u:' + u.id, name: u.name || '', group: acc.name || '', balance: u.balance, kind: 'unit' });
    });
  });
  (directs || []).forEach(function (d) {
    out.push({ v: d.unitId ? 'u:' + d.unitId : 'a:' + d.accountId, name: d.name || '', group: '', balance: d.balance, kind: 'direct' });
  });
  return out;
};

function ClientScopeCombo(props) {
  // 모바일(≤768px) — 데스크탑 렌더 불변.
  //  · wrap 의 minWidth 220 은 헤더 검색바(130px)/스택 폼을 밀어내므로 해제하고 부모 폭을 따른다.
  //  · 드롭다운(width 380)은 화면 밖으로 나가면 안 되고, 모바일에서는 이 콤보가 가로 스크롤 컨테이너
  //    (헤더 좌슬롯 / 입금 이력 표 래퍼) 안에 놓여 absolute 드롭다운이 잘린다 → fixed 로 띄우고 좌우 8px 여백.
  var isMobile = useIsMobile();
  var inputRef = useRef(null);
  var wrapSt = isMobile ? { position: 'relative', minWidth: 0, width: '100%' } : wrap;
  var data = props.data || {};
  var groups = Array.isArray(data.groups) ? data.groups : [];
  var directs = Array.isArray(data.directs) ? data.directs : [];
  var value = props.value || '';
  var onChange = props.onChange || function () {};
  // 빈 값('') 라벨 — 스코프 콤보는 '(전체 거래처)', 입금 귀속 콤보는 '(미지정)' 등 문맥별 재사용.
  var emptyLabel = props.emptyLabel || '(전체 거래처)';
  var [open, setOpen] = useState(false);
  var [q, setQ] = useState('');
  var [openMap, setOpenMap] = useState({}); // 그룹 펼침 — 명시 토글만 기록 (미기록 = 기본 접힘)
  var [hi, setHi] = useState(-1);           // 검색 결과 하이라이트 인덱스 (-1 = 이동 전)
  var hiRef = useRef(null);

  var options = flattenScopeOptions(groups, directs);
  var selected = options.filter(function (o) { return o.v === value; })[0];
  var label = value === 'ledger:'
    ? '(전체 장부)'
    : (value
      ? (selected ? (selected.group ? selected.name + ' · ' + selected.group : selected.name) : '')
      : (props.ledgerRow ? '미수 현황' : emptyLabel));

  var pick = function (v) {
    setQ('');
    setOpen(false);
    setHi(-1);
    onChange(v);
  };

  // 검색 = 내비게이션 — 이름 또는 소속 그룹명 매칭(그룹명 검색 시 하위 전체가 잡힌다)
  var ql = q.trim().toLowerCase();
  // 검색어 일치 구간 하이라이트 (빨간 글자, Jin 8/12)
  var hiName=function(name){
    var n=String(name||'');
    if(!ql)return n;
    var i=n.toLowerCase().indexOf(ql);
    if(i<0)return n;
    return (<span>{n.slice(0,i)}<span style={{color:'#dc2626',fontWeight:700}}>{n.slice(i,i+ql.length)}</span>{n.slice(i+ql.length)}</span>);
  };

  var filtered = ql
    ? options.filter(function (o) {
        return o.name.toLowerCase().indexOf(ql) !== -1 || (o.group || '').toLowerCase().indexOf(ql) !== -1;
      })
    : [];

  // 하이라이트가 목록 밖으로 나가면 그 행만 스크롤 (jsdom 에는 scrollIntoView 가 없어 가드)
  useEffect(function () {
    var el = hiRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [hi]);

  var amountCell = function (bal) {
    return <span style={Object.assign({ color: balColor(bal) }, amtSt)}>{won(bal)}</span>;
  };

  // 선택된 하위 업체가 든 그룹은 기본 펼침 (현재 위치가 목록에서 보이도록). 명시 토글이 우선.
  var selUnit = value.indexOf('u:') === 0 ? value.slice(2) : '';
  var isGroupOpen = function (acc, units) {
    if (openMap[acc.id] !== undefined) return !!openMap[acc.id];
    return units.some(function (u) { return String(u.id) === selUnit; });
  };
  var toggleGroup = function (acc, units) {
    var next = !isGroupOpen(acc, units);
    setOpenMap(function (prev) {
      var n = Object.assign({}, prev);
      n[acc.id] = next;
      return n;
    });
  };

  // 열려 있는 동안의 드롭다운 위치 — 모바일은 입력창 하단에 fixed (스크롤 컨테이너 클리핑 회피)
  var anchor = (isMobile && open && inputRef.current) ? inputRef.current.getBoundingClientRect() : null;
  var listStM = isMobile
    ? Object.assign({}, listSt, { position: 'fixed', top: anchor ? Math.round(anchor.bottom + 4) : 0, left: 8, right: 8, width: 'auto', minWidth: 0, maxWidth: 'none', maxHeight: '50vh', zIndex: 2000 })
    : listSt;
  // 모바일 행 터치 타깃 — 세로 패딩만 키운다(글자 크기·색은 그대로)
  var rowStM = isMobile ? Object.assign({}, rowSt, { padding: '11px 12px' }) : rowSt;
  var groupRowStM = isMobile ? Object.assign({}, groupRowSt, { padding: '11px 12px' }) : groupRowSt;
  var unitRowStM = isMobile ? Object.assign({}, unitRowSt, { padding: '11px 12px', paddingLeft: 33 }) : unitRowSt;

  return (<div style={wrapSt}>
    <input
      ref={inputRef}
      value={open ? q : label}
      onChange={function (e) { setQ(e.target.value); setHi(-1); }}
      onFocus={function () { setOpen(true); setQ(''); setHi(-1); }}
      onBlur={function () { setTimeout(function () { setOpen(false); }, 150); }}
      onKeyDown={function (e) {
        // 화살표/Esc 만 keydown — IME 조합 중에도 커서가 텍스트 끝/처음으로 튀면 안 된다.
        if (e.key === 'Escape') { e.preventDefault(); e.currentTarget.blur(); setOpen(false); return; }
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        if (!ql || !filtered.length) return;
        e.preventDefault();
        var last = filtered.length - 1;
        setHi(e.key === 'ArrowDown'
          ? (hi < 0 ? 0 : Math.min(hi + 1, last))   // 비순환 — 끝에서 멈춘다
          : (hi <= 0 ? 0 : hi - 1));
      }}
      onKeyUp={function (e) {
        // keyup 기준 — 한글 IME 조합 확정용 엔터(keydown 은 isComposing 으로 소비됨)도 잡는다.
        if (e.key !== 'Enter') return;
        // 방향키로 이동했으면 그 항목, 이동 전이면 최상단 결과 (Jin 9/2)
        if (ql && filtered.length) pick(filtered[hi >= 0 && hi < filtered.length ? hi : 0].v);
        e.currentTarget.blur(); setOpen(false); // 결과 수와 무관하게 엔터 = 포커스 완전 해제
      }}
      placeholder={props.placeholder || '거래처 검색...'}
      style={inputSt} />
    {/* searchOnly — 빈 입력 상태에서는 목록을 펼치지 않는다(전체 목록은 드로어 몫). 입력 시 검색 결과만. */}
    {!!open && (!props.searchOnly || !!ql) && <div style={listStM}>
      {!ql && <div style={Object.assign({}, rowStM, { color: '#64748b', fontWeight: value === '' ? 700 : 400 })}
        onMouseDown={function () { pick(''); }}>{props.ledgerRow ? '📊 미수 현황' : emptyLabel}</div>}
      {!ql && !!props.ledgerRow && <div style={Object.assign({}, rowStM, { color: '#64748b', fontWeight: value === 'ledger:' ? 700 : 400 })}
        onMouseDown={function () { pick('ledger:'); }}>(전체 장부)</div>}

      {/* 검색 중 — 그룹 구조 대신 평면 결과 + '이름 · 그룹명' 꼬리표 */}
      {!!ql && filtered.map(function (o, i) {
        var on = i === hi;
        return (<div key={o.v} ref={on ? hiRef : null} data-hi={on ? '1' : undefined}
          style={Object.assign({}, rowStM, { fontWeight: o.v === value ? 700 : 400 }, on ? { background: '#eff6ff' } : null)}
          onMouseDown={function () { pick(o.v); }}>
          <span>{hiName(o.name)}</span>
          {o.kind === 'group'
            ? <span style={tailSt}>▸ {o.unitsCount}곳</span>
            : (o.group ? <span style={tailSt}>· {o.group}</span> : null)}
          {amountCell(o.balance)}
        </div>);
      })}
      {!!ql && filtered.length === 0 && <div style={{ padding: 10, color: '#94a3b8', fontSize: 12 }}>검색 결과 없음</div>}

      {/* 목록(검색어 없음) — 그룹 헤더 + 들여쓴 하위, 그 뒤 직거래처 평면 */}
      {!ql && groups.map(function (g) {
        var acc = (g && g.account) || {};
        var units = (g && g.units) || [];
        var gv = 'a:' + acc.id;
        var gOpen = isGroupOpen(acc, units);
        return (<div key={gv}>
          <div style={Object.assign({}, groupRowStM, { textDecoration: gv === value ? 'underline' : 'none' })}
            onMouseDown={function () { pick(gv); }}>
            {/* 토글 — mousedown 기본동작(입력 blur)과 행 선택을 둘 다 막고 펼침만 바꾼다 */}
            <button type="button" style={toggleSt} title={gOpen ? '접기' : '펼치기'}
              onMouseDown={function (e) { e.preventDefault(); e.stopPropagation(); toggleGroup(acc, units); }}>{gOpen ? '▾' : '▸'}</button>
            <span>{acc.name} <span style={{ fontWeight: 400, color: '#475569' }}>{acc.unitsCount || units.length}곳</span></span>
            <span style={Object.assign({ color: balColor(acc.balance) }, amtSt)}>미수 {won(acc.balance)}</span>
          </div>
          {!!gOpen && units.map(function (u) {
            var uv = 'u:' + u.id;
            return (<div key={uv} style={Object.assign({}, unitRowStM, { fontWeight: uv === value ? 700 : 400 })}
              onMouseDown={function () { pick(uv); }}>
              <span>{u.name}</span>
              {amountCell(u.balance)}
            </div>);
          })}
        </div>);
      })}
      {!ql && directs.map(function (d) {
        var dv = d.unitId ? 'u:' + d.unitId : 'a:' + d.accountId; // 단위 없는 직거래 계정(예: 백필 (미지정)) 은 계정 스코프로
        return (<div key={dv} style={Object.assign({}, rowStM, { fontWeight: dv === value ? 700 : 400 })}
          onMouseDown={function () { pick(dv); }}>
          <span>{d.name}</span>
          {amountCell(d.balance)}
        </div>);
      })}
      {!ql && groups.length === 0 && directs.length === 0 &&
        <div style={{ padding: 10, color: '#94a3b8', fontSize: 12 }}>등록된 거래처가 없습니다</div>}
    </div>}
  </div>);
}

export default ClientScopeCombo;
